import { useEffect, useMemo, useState } from "react";
import usePersistentFilters, { usePersistentState } from "../Common/usePersistentFilters";
import { Badge, Button, Card, Col, Dropdown, Form, Modal, Row, Spinner } from "react-bootstrap";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import swal from "sweetalert";
import api, { getFileUrl } from "../../../api";
import CanAccess from "../../../routes/CanAccess";
import PageTitle from "../../layouts/PageTitle";
import AppPagination from "../Common/AppPagination";

/* =============================
 ROUTE AVAILABILITY MAP
============================= */
const ROUTE_LOOKUP = {
  DPR: "dpr",
  WIR: "wir",
  MIR: "mir",
  "JMR Summary": "jmr",
  "TC/Warranty Card": "tc-documents",
  Drawing: "documents",
  "TDS/Manual": "tds",
  PO: "purchase-orders",
  "JMR Sheets": "measurement-sheets",
  GC: "work-orders/:id/gantt",
  BOQA: "boq/amendment/list",
  HIRA: "documents",
  MOS: "documents",
  MOM: "mom/list",
  WO: "work-orders",
  Indent: "indent",
  DC: "delivery",
  "Contractor DC": "contractor-delivery",
  "Testing & Commissioning": "tc-report",
  "T & C Report": "tc-report",
  "Handover Documents": "handover",
  "Contractor WO": "contractual-debit",
  "Contractor Billing": "contractor-billing",
  "Vendor Billing": "vendor-work-order-billing",
  Reconciliation: "reconciliation",
  Documents: "documents",
  Billings: "billings",
};

/* =============================
 MODULE PERMISSION MAP
============================= */
const MODULE_PERMISSIONS = {
  DPR: "dprs",
  WIR: "wirs",
  MIR: "mirs",
  "JMR Summary": "jmrs",
  "JMR Sheets": "jmrs",
  "TC/Warranty Card": "tcs",
  Drawing: "drawings",
  "TDS/Manual": "tds",
  PO: "pos",
  GC: "gantt",
  BOQA: "workorders",
  HIRA: "hira",
  MOS: "mos",
  MOM: "moms",
  WO: "workorders",
  Indent: "indents",
  DC: "deliveries",
  "Contractor DC": "contractor-deliveries",
  "Testing & Commissioning": "tcs",
  "T & C Report": "tcs",
  "Handover Documents": "handovers",
  "Contractor WO": "contractualwo",
  "Contractor Billing": "contractualwo-billings",
  "Vendor Billing": "vendor-work-order-billings",
  Reconciliation: "reconciliations",
  Documents: "documents",
  Billings: "billings",
};

const WorkOrderCards = ({ onViewModeChange }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const user = useSelector((state) => state.auth?.userDetails);
  const currentRole = String(user?.role?.name || user?.roleName || user?.role || "").toLowerCase();
  const canAddWO = !["client", "po"].includes(currentRole);

  const queryParams = new URLSearchParams(location.search);
  const projectFromUrl = queryParams.get("project");

  const [workOrders, setWorkOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [customButtons, setCustomButtons] = useState([]);
  const [billings, setBillings] = useState([]);

  // Filters
  const [filters, setFilters, resetFilters] = usePersistentFilters("work_orders_cards_filters", {
    search: "",
    client: "",
    project: projectFromUrl || "",
    status: "In Progress",
    billingStatus: "",
  });

  const [currentPage, setCurrentPage] = usePersistentState("work_orders_cards_page", 1);
  const itemsPerPage = 9;

  // Delete Modal
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [woToDelete, setWoToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  /* =============================
     LOAD DATA
  ============================= */
  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);

        const woUrl = projectFromUrl
          ? `/work-orders?project=${projectFromUrl}&limit=1000`
          : `/work-orders?limit=1000`;

        const [woRes, menuRes] = await Promise.all([
          api.get(woUrl),
          api.get("/project-menu-buttons").catch(() => ({ data: { data: [] } })),
        ]);

        const rawWos = woRes.data?.data || [];
        setWorkOrders(rawWos);
        setCustomButtons(menuRes.data?.data || []);

        // Billing permission check
        const normalize = (str) => (str || "").toLowerCase().replace(/[:\s_-]/g, "");
        const userPerms = (user?.permissionNames || []).map((p) => normalize(p));
        const hasBillingPerm =
          userPerms.includes("readbillings") ||
          userPerms.includes("viewbillings") ||
          userPerms.includes("billing") ||
          currentRole === "admin";

        if (hasBillingPerm) {
          try {
            const billingRes = await api.get("/billing");
            setBillings(billingRes.data?.data || []);
          } catch (err) {
            console.error("Billing fetch failed:", err);
            setBillings([]);
          }
        } else {
          setBillings([]);
        }
      } catch (err) {
        console.error("Work orders load failed:", err);
        setWorkOrders([]);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [user, projectFromUrl]);

  /* =============================
     FILTER OPTIONS
  ============================= */
  const clientOptions = useMemo(() => {
    const map = new Map();
    workOrders.forEach((wo) => {
      const client = wo?.client;
      const clientId = client?._id ? String(client._id) : "";
      if (!clientId) return;
      if (!map.has(clientId)) {
        map.set(clientId, {
          value: clientId,
          label: client.companyName || client.name || "Client",
        });
      }
    });
    return [...map.values()].sort((a, b) => a.label.localeCompare(b.label));
  }, [workOrders]);

  const projectOptions = useMemo(() => {
    const map = new Map();
    workOrders.forEach((wo) => {
      const project = wo?.project;
      const projectId = project?._id ? String(project._id) : "";
      if (!projectId) return;
      if (!map.has(projectId)) {
        map.set(projectId, {
          value: projectId,
          label: project.projectName || project.projectCode || "Project",
        });
      }
    });
    return [...map.values()].sort((a, b) => a.label.localeCompare(b.label));
  }, [workOrders]);

  /* =============================
     STATUS & BILLING HELPERS
  ============================= */
  const getWorkOrderBillingStatus = (wo) => {
    const totalValue = Number(wo.totalAmount || wo.value || 0);
    const woId = String(wo._id);
    const billed = billings.reduce((sum, b) => {
      const billingWoId = String(typeof b.workOrder === "object" ? b.workOrder?._id : b.workOrder || "");
      if (billingWoId !== woId) return sum;
      return sum + Number(b.totalAmount || 0);
    }, 0);

    if (billed <= 0) return "Unbilled";
    if (!totalValue) return "Billed";
    if (billed >= totalValue) return "Fully Billed";
    return "Partially Billed";
  };

  const getWorkOrderCompletion = (wo) => {
    const totalValue = Number(wo.totalAmount || wo.value || 0);
    const woId = String(wo._id);
    let billed = 0;
    billings.forEach((b) => {
      const billingWoId = String(typeof b.workOrder === "object" ? b.workOrder?._id : b.workOrder || "");
      if (billingWoId === woId) {
        billed += Number(b.totalAmount || 0);
      }
    });

    if (!totalValue) return wo.status === "Completed" ? 100 : 0;
    const percent = (billed / totalValue) * 100;
    return Math.min(100, Math.round(percent));
  };

  const statusVariant = (s) =>
    s === "Completed"
      ? "success"
      : s === "In Progress"
      ? "info"
      : s === "Delayed"
      ? "danger"
      : "secondary";

  const getClientLogoUrl = (wo) => {
    const client = wo?.client || {};
    return getFileUrl(
      client.image ||
      client.logo ||
      client.brandLogo ||
      client.avatar ||
      client.profileImage ||
      client.photo ||
      ""
    );
  };

  const getWorkOrderDates = (wo) => {
    if (!wo.startDate && !wo.endDate) return wo.totalDays ? `${wo.totalDays} Days` : "-";
    const start = wo.startDate ? new Date(wo.startDate).toLocaleDateString("en-IN") : "Start";
    const end = wo.endDate ? new Date(wo.endDate).toLocaleDateString("en-IN") : "End";
    return `${start} – ${end}`;
  };

  /* =============================
     FILTERED DATA
  ============================= */
  const filteredWorkOrders = useMemo(() => {
    const search = String(filters.search || "").trim().toLowerCase();

    return workOrders.filter((wo) => {
      const billingStatus = getWorkOrderBillingStatus(wo);
      const clientId = String(wo?.client?._id || "");
      const projectId = String(wo?.project?._id || "");
      const woNo = String(wo?.woNo || "").toLowerCase();
      const label = String(wo?.label || "").toLowerCase();
      const projectName = String(wo?.project?.projectName || "").toLowerCase();
      const projectCode = String(wo?.project?.projectCode || "").toLowerCase();
      const clientName = String(wo?.client?.companyName || wo?.client?.name || "").toLowerCase();

      if (filters.client && clientId !== filters.client) return false;
      if (filters.project && projectId !== filters.project) return false;
      // "In Progress" filter groups: In Progress, Pending, Delayed, etc. (everything not Completed)
      if (filters.status === "Completed" && wo.status !== "Completed") return false;
      if (filters.status === "In Progress" && wo.status === "Completed") return false;
      if (filters.billingStatus && billingStatus !== filters.billingStatus) return false;

      if (
        search &&
        !woNo.includes(search) &&
        !label.includes(search) &&
        !projectName.includes(search) &&
        !projectCode.includes(search) &&
        !clientName.includes(search)
      ) {
        return false;
      }

      return true;
    });
  }, [workOrders, filters, billings]);

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredWorkOrders.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredWorkOrders.length / itemsPerPage);

  useEffect(() => {
    setCurrentPage(1);
  }, [filters.search, filters.client, filters.project, filters.status, filters.billingStatus]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const clearFilters = () => {
    setFilters({
      search: "",
      client: "",
      project: "",
      status: "In Progress",
      billingStatus: "",
    });
  };

  /* =============================
     MODULE NAVIGATION
  ============================= */
  const handleModuleClick = (wo, module) => {
    const projectId = wo.project?._id || "";
    const woId = wo._id;

    if (module === "GC") {
      navigate(`/work-orders/${woId}/gantt${projectId ? `?project=${projectId}` : ""}`);
      return;
    }

    if (module === "WO") {
      navigate(`/work-orders/details/${woId}${projectId ? `?project=${projectId}` : ""}`);
      return;
    }

    if (module === "Drawing" || module === "HIRA" || module === "MOS") {
      navigate(`/documents?workOrder=${woId}&documentType=${module}${projectId ? `&project=${projectId}` : ""}`);
      return;
    }

    if (module === "Vendor Billing") {
      navigate(`/vendor-work-order-billing?workOrder=${woId}${projectId ? `&project=${projectId}` : ""}`);
      return;
    }

    if (module === "Billings") {
      navigate(`/billings?workOrder=${woId}${projectId ? `&project=${projectId}` : ""}`);
      return;
    }

    const route = ROUTE_LOOKUP[module];
    if (route) {
      navigate(`/${route}?project=${projectId}&workOrder=${woId}`);
    }
  };

  /* =============================
     DELETE WORK ORDER
  ============================= */
  const handleDeleteClick = (wo) => {
    setWoToDelete(wo);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!woToDelete) return;
    try {
      setIsDeleting(true);
      const res = await api.delete(`/work-orders/${woToDelete._id}`);
      if (res.data?.success) {
        setWorkOrders((prev) => prev.filter((w) => w._id !== woToDelete._id));
        setDeleteModalOpen(false);
        setWoToDelete(null);
        swal("Deleted!", "Work order deleted successfully.", "success");
      } else {
        swal("Error", res.data?.message || "Failed to delete work order.", "error");
      }
    } catch (err) {
      swal("Error", err.response?.data?.message || "Failed to delete work order.", "error");
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <PageTitle activeMenu="Work Orders" motherMenu="Work Orders" />

      {/* HEADER CONTROLS */}
      <Row className="mb-3 align-items-center">
        <Col xs={12} md={7} className="d-flex align-items-center flex-wrap gap-3 mb-2 mb-md-0">
          <h4 className="fw-bold mb-0">Work Orders</h4>

          {/* View Mode Toggle */}
          <div
            className="d-inline-flex align-items-center bg-white border rounded-3 p-1 shadow-sm"
            style={{ height: "40px", boxSizing: "border-box" }}
          >
            <button
              type="button"
              className="btn btn-primary text-white shadow-sm d-inline-flex align-items-center justify-content-center px-3 border-0"
              style={{
                height: "32px",
                fontSize: "0.85rem",
                fontWeight: 500,
                borderRadius: "6px",
                gap: "8px",
                lineHeight: 1,
              }}
              onClick={() => {
                localStorage.setItem("workOrderViewMode", "cards");
                if (onViewModeChange) onViewModeChange("cards");
              }}
            >
              <i className="fas fa-th-large" style={{ fontSize: "0.85rem" }}></i>
              <span>Cards View</span>
            </button>
            <button
              type="button"
              className="btn btn-light text-muted bg-transparent d-inline-flex align-items-center justify-content-center px-3 border-0 ms-1"
              style={{
                height: "32px",
                fontSize: "0.85rem",
                fontWeight: 500,
                borderRadius: "6px",
                gap: "8px",
                lineHeight: 1,
              }}
              onClick={() => {
                localStorage.setItem("workOrderViewMode", "table");
                if (onViewModeChange) {
                  onViewModeChange("table");
                } else {
                  navigate(projectFromUrl ? `/work-orders?project=${projectFromUrl}` : "/work-orders");
                }
              }}
            >
              <i className="fas fa-list" style={{ fontSize: "0.85rem" }}></i>
              <span>Table View</span>
            </button>
          </div>
        </Col>

        <Col xs={12} md={5} className="text-md-end">
          {canAddWO && (
            <Button
              variant="primary"
              className="d-inline-flex align-items-center justify-content-center px-3 shadow-sm border-0"
              style={{
                height: "40px",
                fontSize: "0.85rem",
                fontWeight: 500,
                gap: "6px",
                borderRadius: "8px",
              }}
              onClick={() =>
                navigate(projectFromUrl ? `/work-orders/add?project=${projectFromUrl}` : "/work-orders/add")
              }
            >
              <i className="fas fa-plus"></i>
              <span>New Work Order</span>
            </Button>
          )}
        </Col>
      </Row>

      {/* FILTER BAR */}
      <Card className="shadow-sm border-0 mb-3">
        <Card.Body>
          <Row className="g-2">
            <Col xl={3} lg={6} md={6}>
              <Form.Control
                name="search"
                value={filters.search}
                onChange={handleFilterChange}
                placeholder="Search WO No, Project, Client..."
              />
            </Col>
            <Col xl={2} lg={6} md={6}>
              <Form.Select name="client" value={filters.client} onChange={handleFilterChange}>
                <option value="">All Clients</option>
                {clientOptions.map((client) => (
                  <option key={client.value} value={client.value}>
                    {client.label}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col xl={2} lg={6} md={6}>
              <Form.Select name="project" value={filters.project} onChange={handleFilterChange}>
                <option value="">All Projects</option>
                {projectOptions.map((proj) => (
                  <option key={proj.value} value={proj.value}>
                    {proj.label}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col xl={2} lg={6} md={6}>
              <Form.Select name="status" value={filters.status} onChange={handleFilterChange}>
                <option value="">All Status</option>
                <option value="In Progress">In Progress</option>
                <option value="Completed">Completed</option>
              </Form.Select>
            </Col>
            <Col xl={2} lg={6} md={6}>
              <Form.Select
                name="billingStatus"
                value={filters.billingStatus}
                onChange={handleFilterChange}
              >
                <option value="">Billing Status</option>
                <option value="Unbilled">Unbilled</option>
                <option value="Partially Billed">Partially Billed</option>
                <option value="Fully Billed">Fully Billed</option>
                <option value="Billed">Billed</option>
              </Form.Select>
            </Col>
            <Col xl={1} lg={12} md={12} className="d-grid">
              <Button variant="outline-secondary" onClick={clearFilters}>
                Reset
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* CARDS GRID */}
      {loading ? (
        <div className="text-center my-5 py-5">
          <Spinner animation="border" variant="primary" style={{ width: "3rem", height: "3rem" }} />
          <p className="text-muted fw-semibold mt-3">Loading work orders...</p>
        </div>
      ) : filteredWorkOrders.length === 0 ? (
        <Card className="text-center py-5 shadow-sm border-0">
          <Card.Body>
            <i className="fas fa-folder-open text-muted fa-3x mb-3"></i>
            <h5 className="text-muted">No Work Orders Found</h5>
            <p className="text-muted small">Try adjusting your search or filters.</p>
            {canAddWO && (
              <Button
                variant="primary"
                size="sm"
                onClick={() =>
                  navigate(projectFromUrl ? `/work-orders/add?project=${projectFromUrl}` : "/work-orders/add")
                }
              >
                + Add Work Order
              </Button>
            )}
          </Card.Body>
        </Card>
      ) : (
        <>
          <Row className="g-3">
            {currentItems.map((wo) => {
              const billingStatus = getWorkOrderBillingStatus(wo);
              const percent = getWorkOrderCompletion(wo);
              const totalAmount = Number(wo.totalAmount || wo.value || 0);

              return (
                <Col key={wo._id} xs={12} md={6} xl={4}>
                  <Card className="h-100 shadow-sm border-0" style={{ borderRadius: "12px" }}>
                    <Card.Body style={{ position: "relative", paddingTop: "2.75rem" }}>
                      {/* 3-DOTS ACTION MENU */}
                      <div style={{ position: "absolute", top: "0.6rem", right: "0.6rem", zIndex: 2 }}>
                        <Dropdown align="end">
                          <Dropdown.Toggle size="sm" className="i-false btn-light border-0 shadow-none">
                            <i className="fas fa-ellipsis-v text-muted"></i>
                          </Dropdown.Toggle>
                          <Dropdown.Menu>
                            <Dropdown.Item onClick={() => navigate(`/work-orders/details/${wo._id}`)}>
                              <i className="fas fa-eye me-2 text-primary"></i>View Details
                            </Dropdown.Item>

                            <CanAccess allowedRoles={["Admin", "Manager"]} permission="edit:workorders">
                              <Dropdown.Item onClick={() => navigate(`/work-orders/edit/${wo._id}`)}>
                                <i className="fas fa-edit me-2 text-info"></i>Edit Work Order
                              </Dropdown.Item>
                            </CanAccess>

                            <Dropdown.Item onClick={() => navigate(`/work-orders/${wo._id}/boq`)}>
                              <i className="fas fa-calculator me-2 text-success"></i>BOQ
                            </Dropdown.Item>

                            <Dropdown.Item onClick={() => navigate(`/work-orders/${wo._id}/gantt`)}>
                              <i className="fas fa-chart-gantt me-2 text-warning"></i>Gantt Chart
                            </Dropdown.Item>

                            <Dropdown.Item onClick={() => navigate(`/billings?workOrder=${wo._id}`)}>
                              <i className="fas fa-file-invoice-dollar me-2 text-secondary"></i>Billings
                            </Dropdown.Item>

                            <Dropdown.Item onClick={() => navigate(`/delivery?workOrder=${wo._id}`)}>
                              <i className="fas fa-truck me-2 text-info"></i>Deliveries
                            </Dropdown.Item>

                            <Dropdown.Item onClick={() => navigate(`/documents?workOrder=${wo._id}`)}>
                              <i className="fas fa-folder-open me-2 text-primary"></i>Documents
                            </Dropdown.Item>

                            <CanAccess allowedRoles={["Admin"]} permission="delete:workorders">
                              <>
                                <Dropdown.Divider />
                                <Dropdown.Item className="text-danger" onClick={() => handleDeleteClick(wo)}>
                                  <i className="fas fa-trash me-2"></i>Delete
                                </Dropdown.Item>
                              </>
                            </CanAccess>
                          </Dropdown.Menu>
                        </Dropdown>
                      </div>

                      {/* HEADER */}
                      <div className="d-flex justify-content-between align-items-start gap-1">
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <h6 className="fw-bold mb-1 text-primary" style={{ lineHeight: 1.2 }}>
                            {wo.label || wo.woNo || "Work Order"}
                          </h6>
                          <div className="text-muted small mb-1" style={{ fontWeight: 600 }}>
                            <i className="fas fa-sitemap me-1 opacity-60"></i>
                            {wo.woNo || "—"}
                          </div>
                          <div className="text-dark fw-medium small mb-1">
                            <i className="fas fa-project-diagram me-1 text-muted"></i>
                            {wo.project?.projectName || wo.projectName || "Project"}
                            {wo.project?.projectCode && (
                              <span className="text-muted ms-1">({wo.project.projectCode})</span>
                            )}
                          </div>
                          <div className="text-muted small">
                            <i className="fas fa-building me-1 opacity-75"></i>
                            {wo.client?.companyName || wo.client?.name || "Client"}
                          </div>
                        </div>

                        {/* CLIENT LOGO */}
                        <div className="d-flex flex-column align-items-end gap-1">
                          <div
                            title={wo.client?.companyName || wo.client?.name || "Client"}
                            style={{
                              width: 65,
                              height: 65,
                              border: "1px solid #e5e8ef",
                              borderRadius: 12,
                              overflow: "hidden",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              background: "#fff",
                              flexShrink: 0,
                              marginRight: "20px",
                              marginTop: "0.2rem",
                            }}
                          >
                            {getClientLogoUrl(wo) ? (
                              <img
                                src={getClientLogoUrl(wo)}
                                alt={wo.client?.companyName || wo.client?.name || "Client Logo"}
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "contain",
                                  padding: 6,
                                }}
                              />
                            ) : (
                              <div
                                className="text-center text-muted fw-bold"
                                style={{ fontSize: 11, padding: 6, lineHeight: 1.1 }}
                              >
                                {(wo.client?.companyName || wo.client?.name || "CL")
                                  .split(" ")
                                  .slice(0, 2)
                                  .map((part) => part?.[0]?.toUpperCase() || "")
                                  .join("") || "WO"}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* DATES BADGE */}
                      <div className="mt-2">
                        <Badge bg="primary" className="rounded-pill px-3 py-1 fw-normal">
                          <i className="fas fa-calendar-alt me-1"></i>
                          {getWorkOrderDates(wo)}
                        </Badge>
                      </div>

                      {/* STATUS & BILLING BADGES */}
                      <div className="mt-2 d-flex flex-wrap align-items-center gap-1">
                        <Badge bg={statusVariant(wo.status)} className="rounded-pill px-2 py-1">
                          {wo.status || "Pending"}
                        </Badge>
                        <Badge
                          bg={
                            billingStatus === "Fully Billed"
                              ? "success"
                              : billingStatus === "Partially Billed"
                              ? "warning"
                              : billingStatus === "Billed"
                              ? "info"
                              : "secondary"
                          }
                          className="rounded-pill px-2 py-1"
                        >
                          {billingStatus}
                        </Badge>
                        {totalAmount > 0 && (
                          <Badge bg="light" text="dark" className="border rounded-pill px-2 py-1 ms-auto fw-bold">
                            ₹{totalAmount.toLocaleString("en-IN")}
                          </Badge>
                        )}
                      </div>

                      {/* CIRCULAR PROGRESS */}
                      <div className="d-flex justify-content-center mt-3">
                        <div
                          className="circular-progress"
                          style={{
                            "--value": percent,
                            "--progress-color":
                              percent === 100 ? "#198754" : percent > 0 ? "#ffc107" : "#dc3545",
                          }}
                        >
                          <span>{percent}%</span>
                        </div>
                      </div>

                      {/* QUICK TOP SHORTCUT BUTTON */}
                      <div className="d-flex gap-2 mt-3">
                        <Button
                          size="sm"
                          variant="outline-primary"
                          className="w-100"
                          onClick={() => navigate(`/work-orders/${wo._id}/boq`)}
                        >
                          <i className="fas fa-calculator me-2"></i>BOQ Details
                        </Button>
                      </div>

                      {/* =============================
                          MODULE SECTIONS & BUTTONS
                          (Same permissions & categories as ProjectList)
                      ============================= */}
                      <WorkOrderSection
                        title="Reports & Drawings"
                        items={[
                          "DPR",
                          "WIR",
                          "MIR",
                          "JMR Summary",
                          "Drawing",
                          "TDS/Manual",
                          "TC/Warranty Card",
                          "JMR Sheets",
                          "Documents",
                        ]}
                        customButtons={customButtons.filter((b) => b.section === "Reports & Drawings")}
                        workOrder={wo}
                        onClick={(m) => handleModuleClick(wo, m)}
                      />

                      <WorkOrderSection
                        title="Planning & Control"
                        items={["GC", "BOQA"]}
                        customButtons={customButtons.filter((b) => b.section === "Planning & Control")}
                        workOrder={wo}
                        onClick={(m) => handleModuleClick(wo, m)}
                      />

                      <WorkOrderSection
                        title="Safety & Docs"
                        items={["HIRA", "MOS"]}
                        customButtons={customButtons.filter((b) => b.section === "Safety & Docs")}
                        workOrder={wo}
                        onClick={(m) => handleModuleClick(wo, m)}
                      />

                      <WorkOrderSection
                        title="Execution"
                        items={["MOM", "Indent", "DC", "Contractor DC"]}
                        customButtons={customButtons.filter((b) => b.section === "Execution")}
                        workOrder={wo}
                        onClick={(m) => handleModuleClick(wo, m)}
                      />

                      <WorkOrderSection
                        title="Quality & Closure"
                        items={["T & C Report", "Handover Documents"]}
                        customButtons={customButtons.filter((b) => b.section === "Quality & Closure")}
                        workOrder={wo}
                        onClick={(m) => handleModuleClick(wo, m)}
                      />

                      <WorkOrderSection
                        title="Commercial"
                        highlight
                        items={[
                          "Contractor WO",
                          "Contractor Billing",
                          "Vendor Billing",
                          "Reconciliation",
                          "Billings",
                        ]}
                        customButtons={customButtons.filter((b) => b.section === "Commercial")}
                        workOrder={wo}
                        onClick={(m) => handleModuleClick(wo, m)}
                      />

                      {/* Dynamic custom sections */}
                      {[...new Set(customButtons.map((b) => b.section))]
                        .filter(
                          (s) =>
                            ![
                              "Reports & Drawings",
                              "Planning & Control",
                              "Safety & Docs",
                              "Execution",
                              "Quality & Closure",
                              "Commercial",
                            ].includes(s)
                        )
                        .map((s) => (
                          <WorkOrderSection
                            key={s}
                            title={s}
                            items={[]}
                            customButtons={customButtons.filter((b) => b.section === s)}
                            workOrder={wo}
                            onClick={(m) => handleModuleClick(wo, m)}
                          />
                        ))}
                    </Card.Body>
                  </Card>
                </Col>
              );
            })}
          </Row>

          {/* PAGINATION */}
          <div className="mt-4">
            <AppPagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalCount={filteredWorkOrders.length}
              itemsLength={currentItems.length}
              limit={itemsPerPage}
              onPageChange={(page) => setCurrentPage(page)}
            />
          </div>
        </>
      )}

      {/* DELETE CONFIRMATION MODAL */}
      <Modal show={deleteModalOpen} onHide={() => !isDeleting && setDeleteModalOpen(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Delete Work Order</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-1">
            Are you sure you want to delete work order <b>{woToDelete?.woNo}</b>?
          </p>
          <p className="text-muted small">
            Project: {woToDelete?.project?.projectName || woToDelete?.projectName || "N/A"}
          </p>
          <div className="alert alert-warning py-2 px-3 mt-2 mb-0">
            <i className="fas fa-exclamation-triangle me-2"></i>
            This action cannot be undone.
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setDeleteModalOpen(false)} disabled={isDeleting}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleConfirmDelete} disabled={isDeleting}>
            {isDeleting ? "Deleting..." : "Delete Work Order"}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

/* =============================
 SECTION COMPONENT FOR WORK ORDER
============================= */
const WorkOrderSection = ({ title, items, highlight, onClick, customButtons = [], workOrder }) => {
  const navigate = useNavigate();
  const user = useSelector((state) => state.auth?.userDetails);

  // Check which static items are visible for this user
  const visibleItems = items.filter((item) => {
    const routeEnabled = Boolean(ROUTE_LOOKUP[item]);
    const permissionKey = MODULE_PERMISSIONS[item];
    if (!routeEnabled || !permissionKey) return false;

    let roleName = "";
    if (typeof user?.role === "string") roleName = user.role;
    else if (typeof user?.role === "object") roleName = user?.role?.name || "";
    if (roleName.toLowerCase() === "admin") return true;

    const normalize = (str) => (str || "").toLowerCase().replace(/[:\s_-]/g, "");
    const userPerms = (user?.permissionNames || []).map((p) => normalize(p));
    const normalizedReq = normalize(`read:${permissionKey}`);

    return (
      userPerms.includes(normalizedReq) ||
      userPerms.includes(`read${normalizedReq}`) ||
      userPerms.includes(`view${normalizedReq}`)
    );
  });

  // Check which custom buttons are visible
  const visibleCustom = customButtons.filter((btn) => {
    if (!btn.permission) return true;

    let roleName = "";
    if (typeof user?.role === "string") roleName = user.role;
    else if (typeof user?.role === "object") roleName = user?.role?.name || "";
    if (roleName.toLowerCase() === "admin") return true;

    const normalize = (str) => (str || "").toLowerCase().replace(/[:\s_-]/g, "");
    const userPerms = (user?.permissionNames || []).map((p) => normalize(p));
    return userPerms.includes(normalize(btn.permission));
  });

  if (visibleItems.length === 0 && visibleCustom.length === 0) return null;

  const handleCustomClick = (btn) => {
    let url = (btn.navigateTo || "")
      .replace("{projectId}", workOrder?.project?._id || "")
      .replace("{workOrderId}", workOrder?._id || "");
    navigate(url);
  };

  return (
    <div className="mt-3">
      <div className="fw-semibold small text-muted text-uppercase" style={{ letterSpacing: "0.5px" }}>
        {title}
      </div>
      <div className="d-flex flex-wrap gap-2 mt-1">
        {/* Static buttons */}
        {items.map((item) => {
          const routeEnabled = Boolean(ROUTE_LOOKUP[item]);
          const permissionKey = MODULE_PERMISSIONS[item];
          if (!routeEnabled || !permissionKey) return null;

          const multiPerms = item === "WO" ? ["read:wo"] : null;
          const permission = `read:${permissionKey}`;

          return (
            <CanAccess
              key={item}
              permissions={multiPerms || []}
              permission={multiPerms ? "" : permission}
              fallback={null}
            >
              <Button
                size="sm"
                variant={highlight ? "warning" : "outline-primary"}
                onClick={() => onClick(item)}
                style={{ fontSize: "0.78rem", padding: "3px 8px" }}
              >
                {item}
              </Button>
            </CanAccess>
          );
        })}

        {/* Dynamic custom buttons */}
        {visibleCustom.map((btn) => (
          <Button
            key={btn._id}
            size="sm"
            variant={btn.isHighlight ? "warning" : highlight ? "warning" : "outline-success"}
            onClick={() => handleCustomClick(btn)}
            title={btn.navigateTo}
            style={{ fontSize: "0.78rem", padding: "3px 8px" }}
          >
            {btn.label}
          </Button>
        ))}
      </div>
    </div>
  );
};

export default WorkOrderCards;
